/**
 * Custom report template builder — edit name/description and ordered fields.
 * Single interaction pattern (↑/↓ buttons + expand to edit) works on mobile
 * and desktop without a DnD library.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Loader2, Plus, Save, FileText, Eye, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import {
  getTemplate, updateTemplate, listFields, createField, updateField, deleteField, reorderFields,
  type ReportTemplate, type ReportTemplateField, type FieldType,
} from "@/lib/report-templates";
import { TemplateFieldEditor } from "@/components/reports/TemplateFieldEditor";
import { ReportPreviewDialog } from "@/components/reports/ReportPreviewDialog";

export const Route = createFileRoute("/_authenticated/reports/templates/$id/")({
  component: BuilderPage,
  head: () => ({ meta: [{ title: "Edit template — MissionReady" }] }),
});

const EDIT_ROLES = ["org_owner", "admin", "supervisor"];

function BuilderPage() {
  const { id } = Route.useParams();
  const { currentRole, currentOrg } = useAuth();
  const flags = useFeatureFlags();
  const unlocked = flags.loaded && flags.custom_reports === true;
  const canEdit = unlocked && EDIT_ROLES.includes(currentRole ?? "");
  

  const [tmpl, setTmpl] = useState<ReportTemplate | null>(null);
  const [fields, setFields] = useState<ReportTemplateField[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState<"view" | "publish">("view");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [t, fs] = await Promise.all([getTemplate(id), listFields(id)]);
        if (cancelled) return;
        setTmpl(t);
        setFields(fs);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const setMeta = (patch: Partial<Pick<ReportTemplate, "name" | "description">>) => {
    setTmpl((t) => (t ? { ...t, ...patch } : t));
    setDirty(true);
  };

  const addField = async (type: FieldType = "text") => {
    if (!canEdit || !tmpl) return;
    try {
      const created = await createField({
        template_id: tmpl.id,
        position: fields.length,
        field_type: type,
        label: "",
        help_text: null,
        required: false,
        options: type === "select" || type === "multiselect" ? [{ label: "Option 1", value: "opt_0" }] : [],
      });
      setFields((xs) => [...xs, created]);
    } catch (e) {
      toast.error(`Could not add field: ${String((e as Error).message ?? e)}`);
    }
  };

  const patchField = async (fid: string, patch: Partial<ReportTemplateField>) => {
    setFields((xs) => xs.map((f) => (f.id === fid ? { ...f, ...patch } : f)));
    try {
      await updateField(fid, patch);
    } catch (e) {
      toast.error(`Could not save field: ${String((e as Error).message ?? e)}`);
    }
  };

  const removeField = async (fid: string) => {
    if (!confirm("Remove this field?")) return;
    const prev = fields;
    setFields((xs) => xs.filter((f) => f.id !== fid));
    try {
      await deleteField(fid);
    } catch (e) {
      setFields(prev);
      toast.error(`Could not remove: ${String((e as Error).message ?? e)}`);
    }
  };

  const move = async (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= fields.length) return;
    const next = fields.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    const repos = next.map((f, i) => ({ ...f, position: i }));
    setFields(repos);
    try {
      await reorderFields(repos.map((f) => ({ id: f.id, position: f.position })));
    } catch (e) {
      toast.error(`Could not reorder: ${String((e as Error).message ?? e)}`);
    }
  };

  const saveMeta = async (publish = false) => {
    if (!tmpl) return;
    setSaving(true);
    try {
      await updateTemplate(tmpl.id, {
        name: tmpl.name,
        description: tmpl.description,
        ...(publish ? { status: "published" as const } : {}),
      });
      if (publish) setTmpl({ ...tmpl, status: "published" });
      setDirty(false);
      toast.success(publish ? "Template published" : "Saved");
    } catch (e) {
      toast.error(`Could not save: ${String((e as Error).message ?? e)}`);
    } finally {
      setSaving(false);
    }
  };

  const canPublish = useMemo(
    () => fields.length > 0 && fields.every((f) => f.label.trim().length > 0),
    [fields],
  );

  if (!unlocked && flags.loaded) {
    return (
      <div className="mx-auto max-w-3xl px-4 pb-24 pt-6">
        <p className="text-sm text-muted-foreground">Upgrade to a paid plan to use the builder.</p>
        <Button asChild className="mt-3"><Link to="/reports/templates">Back</Link></Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (!tmpl) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <p className="text-sm text-muted-foreground">Template not found.</p>
        <Button asChild className="mt-3"><Link to="/reports/templates">Back</Link></Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 pb-32 pt-6 md:px-8 md:py-8">
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/reports/templates"><ArrowLeft className="mr-1 h-4 w-4" /> All templates</Link>
      </Button>

      <div className="flex flex-wrap items-center gap-2">
        <FileText className="h-5 w-5 text-primary" />
        <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">Edit template</h1>
        <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
          tmpl.status === "published" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
        }`}>
          {tmpl.status}
        </span>
      </div>

      <div className="mt-5 space-y-3 rounded-2xl border border-border bg-card p-4">
        <div>
          <Label>Name</Label>
          <Input aria-label="Name"
            value={tmpl.name} onChange={(e) => setMeta({ name: e.target.value })}
            disabled={!canEdit} className="mt-1"
          />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea aria-label="Description"
            rows={2} value={tmpl.description ?? ""}
            onChange={(e) => setMeta({ description: e.target.value })}
            disabled={!canEdit} className="mt-1"
          />
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold">Fields</h2>
        <span className="text-xs text-muted-foreground">{fields.length} field{fields.length === 1 ? "" : "s"}</span>
      </div>

      <ul className="mt-3 space-y-2">
        {fields.map((f, i) => (
          <li key={f.id}>
            <TemplateFieldEditor
              field={f}
              isFirst={i === 0}
              isLast={i === fields.length - 1}
              onPatch={(p) => patchField(f.id, p)}
              onMoveUp={() => move(i, -1)}
              onMoveDown={() => move(i, 1)}
              onDelete={() => removeField(f.id)}
            />
          </li>
        ))}
        {fields.length === 0 && (
          <li className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
            No fields yet. Add your first field below.
          </li>
        )}
      </ul>

      {canEdit && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => addField("text")} className="gap-1">
            <Plus className="h-3.5 w-3.5" /> Short text
          </Button>
          <Button size="sm" variant="outline" onClick={() => addField("textarea")}>+ Long text</Button>
          <Button size="sm" variant="outline" onClick={() => addField("select")}>+ Choice</Button>
          <Button size="sm" variant="outline" onClick={() => addField("checkbox")}>+ Yes/no</Button>
          <Button size="sm" variant="outline" onClick={() => addField("date")}>+ Date</Button>
          <Button size="sm" variant="outline" onClick={() => addField("photo")}>+ Photo</Button>
          <Button size="sm" variant="outline" onClick={() => addField("signature")}>+ Signature</Button>
        </div>
      )}

      {/* Sticky action bar */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 px-4 py-3 backdrop-blur md:static md:mt-8 md:rounded-2xl md:border md:bg-card md:p-4">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline" size="sm"
            disabled={fields.length === 0}
            onClick={() => { setPreviewMode("view"); setPreviewOpen(true); }}
          >
            <Eye className="mr-1 h-4 w-4" /> Preview
          </Button>
          {canEdit && (
            <>
              <Button size="sm" variant="secondary" disabled={saving || !dirty} onClick={() => saveMeta(false)}>
                {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
                Save draft
              </Button>
              <Button
                size="sm"
                disabled={saving || !canPublish}
                onClick={() => { setPreviewMode("publish"); setPreviewOpen(true); }}
              >
                <CheckCircle2 className="mr-1 h-4 w-4" />
                {tmpl.status === "published" ? "Save" : "Publish"}
              </Button>
            </>
          )}
        </div>
      </div>

      <ReportPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        templateId={tmpl.id}
        templateName={tmpl.name}
        organizationName={currentOrg?.name}
        fields={fields}
        onConfirm={previewMode === "publish"
          ? async () => { await saveMeta(true); setPreviewOpen(false); }
          : undefined}
        confirmLabel={tmpl.status === "published" ? "Save changes" : "Confirm & publish"}
        confirming={saving}
      />
    </div>
  );
}
